import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Zap, X, Loader2, Check, ArrowUpRight } from "lucide-react";
import { API, useAuth, formatApiError } from "@/contexts/AuthContext";
import { withFpTid } from "@/lib/fprom";

// AI credit balance + top-up entry point. Uses the existing top-up SKUs
// already in tier_catalog.py (ai_social_week / ai_social_month) — no new
// Stripe products/prices are invented. Checkout uses the existing
// POST /api/checkout/tier/session endpoint.
const TOPUP_KEYS = ["ai_social_week", "ai_social_month"];

// Kickstart upgrade path: K1 → K2, K2 → K3. Uses existing lifetime
// Stripe SKUs (kickstart_2 €149, kickstart_3 €199). No new price.
const KICKSTART_UPGRADE = {
  "Kickstart 1": { target: "kickstart_2", label: "Kickstart 2", credits: 150, amount: 149 },
  "Kickstart 2": { target: "kickstart_3", label: "Kickstart 3", credits: 300, amount: 199 },
};

function pctColour(remaining, limit) {
  if (limit === null || limit === undefined) return "#059669"; // unlimited
  if (!limit) return "#DC2626";
  const pct = (remaining / limit) * 100;
  if (pct >= 40) return "#059669";
  if (pct >= 15) return "#D4AF37";
  return "#DC2626";
}

function fmtCredits(v) {
  if (v === null || v === undefined) return "Unlimited";
  return String(v);
}

export default function CreditsCard({ compact = false }) {
  const { user } = useAuth();
  const ownWorkspace = !user?.active_workspace || user.active_workspace === user?.id;
  const tier = user?.tier || {};

  // Hooks must be declared unconditionally before any early return
  // (react-hooks/rules-of-hooks).
  const [open, setOpen] = useState(false);
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);

  // Credits are a Kickstart-only construct. Every other plan (Starter,
  // Creator, Business, Agency, Enterprise, trial, founder) has no
  // credit meter, no top-up buttons, no comparison rows.
  const plan = String(user?.subscription_plan || tier.plan_key || "");
  if (!ownWorkspace || !plan.startsWith("Kickstart")) return null;

  // Kickstart has a finite lifetime allowance. Honor a finite server
  // total so purchased credits are not discarded by the display.
  const KICKSTART_CAP = {
    "Kickstart 1": 50,
    "Kickstart 2": 150,
    "Kickstart 3": 300,
  };
  const kickstartCap = KICKSTART_CAP[plan] || null;

  const rawLimit = tier.ai_credits_limit;
  const limit = Number.isSafeInteger(rawLimit) && rawLimit >= (kickstartCap || 0)
    ? rawLimit : kickstartCap;
  const used = Number.isSafeInteger(tier.ai_credits_used) && tier.ai_credits_used >= 0
    ? tier.ai_credits_used : 0;
  const remaining = Number.isSafeInteger(tier.ai_credits_remaining) && tier.ai_credits_remaining >= 0
    ? tier.ai_credits_remaining : limit == null ? undefined : Math.max(0, limit - used);
  const extraRemaining = tier.ai_credits_extra_remaining || 0;
  const extraExpiry = tier.ai_credits_extra_expires_at
    ? new Date(tier.ai_credits_extra_expires_at) : null;
  const isUnlimited = limit === null || limit === undefined;

  const upgrade = KICKSTART_UPGRADE[plan] || null;
  const doUpgrade = async () => {
    if (!upgrade || upgradeBusy) return;
    setUpgradeBusy(true);
    setUpgradeError(null);
    try {
      const { data } = await axios.post(`${API}/checkout/tier/session`, {
        tier_key: upgrade.target,
        origin_url: window.location.origin,
        consent_waiver: true,
        ...withFpTid(),
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("No checkout URL returned.");
    } catch (e) {
      setUpgradeError(
        formatApiError(e?.response?.data?.detail) || "Could not start upgrade."
      );
      setUpgradeBusy(false);
    }
  };

  return (
    <section
      data-testid="credits-card"
      className="bg-white border border-[#eee] rounded-xl p-5 flex flex-wrap items-center gap-5"
    >
      <span
        className="shrink-0 inline-flex items-center justify-center rounded-lg"
        style={{ background: "#EAF0FF", color: "#202B60", width: 44, height: 44 }}
      >
        <Sparkles size={18} />
      </span>
      <div className="flex-1 min-w-[200px]">
        <p className="text-[11px] uppercase tracking-wider text-[#888] font-semibold">
          AI credits
        </p>
        <p
          className="mt-1 text-[22px] font-bold tracking-tight"
          data-testid="credits-balance"
          style={{ color: pctColour(remaining, limit) }}
        >
          {isUnlimited ? "Unlimited" : `${fmtCredits(remaining)} left`}
          {!isUnlimited && (
            <span className="ml-2 text-[13px] font-medium text-[#666]">
              of {limit}
            </span>
          )}
        </p>
        {!isUnlimited && (
          <div className="mt-2 h-1.5 rounded-full bg-[#F0F0F0] max-w-md overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(100, Math.max(0, (used / limit) * 100))}%`,
                background: pctColour(remaining, limit),
              }}
            />
          </div>
        )}
        {!compact && !isUnlimited && (
          <p className="mt-2 text-[12px] text-[#888]">
            {used} used in total{tier.plan_key ? <> · <span className="notranslate" translate="no">{tier.plan_key}</span></> : ""}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="credits-buy-btn"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#202B60] text-white px-4 py-2 text-[13px] font-semibold hover:opacity-95 transition-opacity"
      >
        <Zap size={13} />
        Buy credits
      </button>

      {extraRemaining > 0 && (
        <p className="basis-full text-[12px] text-[#666]" data-testid="extra-credits-validity">
          {tier.ai_credits_included_remaining ?? 0} included credits · {extraRemaining} extra credits
          {extraExpiry && Number.isFinite(extraExpiry.getTime())
            ? ` · extra credits valid until ${extraExpiry.toLocaleString()}` : ""}
        </p>
      )}

      {upgrade && (
        <div className="basis-full flex flex-wrap items-center justify-between gap-3 pt-4 mt-1 border-t border-[#F0F0F0]">
          <p className="text-[12.5px] text-[#555]">
            Want a larger lifetime allowance?{" "}
            <span className="text-[#0A1628] font-semibold">
              {upgrade.label} unlocks {upgrade.credits} total credits
            </span>{" "}
            — lifetime, one-time €{upgrade.amount}.
          </p>
          <button
            type="button"
            onClick={doUpgrade}
            disabled={upgradeBusy}
            data-testid={`credits-upgrade-${upgrade.target}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37] bg-[#FFF7E0] text-[#8A6D14] px-3.5 py-1.5 text-[12.5px] font-semibold hover:bg-[#FBEFC5] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {upgradeBusy ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <ArrowUpRight size={12} />
            )}
            {upgradeBusy ? "Opening…" : `Upgrade to ${upgrade.label}`}
          </button>
          {upgradeError && (
            <p
              data-testid="credits-upgrade-error"
              className="basis-full text-[12px] text-rose-600"
            >
              {upgradeError}
            </p>
          )}
        </div>
      )}

      {open && <BuyCreditsModal onClose={() => setOpen(false)} />}
    </section>
  );
}

export function BuyCreditsModal({ onClose }) {
  const { user } = useAuth();
  const ownWorkspace = !user?.active_workspace || user.active_workspace === user?.id;
  const [plans, setPlans] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ownWorkspace) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tier/catalog`);
        const filtered = (data?.plans || []).filter((p) =>
          TOPUP_KEYS.includes(p.tier_key)
        );
        // Keep the same order as TOPUP_KEYS
        filtered.sort(
          (a, b) => TOPUP_KEYS.indexOf(a.tier_key) - TOPUP_KEYS.indexOf(b.tier_key)
        );
        setPlans(filtered);
      } catch (e) {
        setError("Could not load top-up options. Please refresh.");
        setPlans([]);
      }
    })();
  }, [ownWorkspace]);

  const buy = async (tier_key) => {
    setBusyKey(tier_key);
    setError(null);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/checkout/tier/session`, {
        tier_key,
        origin_url: origin,
        consent_waiver: true,
        ...withFpTid(),
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("No checkout URL returned.");
    } catch (e) {
      setError(
        formatApiError(e?.response?.data?.detail) || "Could not start checkout."
      );
      setBusyKey(null);
    }
  };

  if (!ownWorkspace) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="buy-credits-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-[18px] font-bold text-[#0A1628]">Buy AI credits</h2>
            <p className="text-[13px] text-[#666] mt-1">
              Top up instantly. Charged once via Stripe.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="buy-credits-close"
            className="p-1.5 rounded hover:bg-[#f5f5f5] text-[#666]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {error && (
          <div
            data-testid="buy-credits-error"
            className="mb-3 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-[13px] px-3 py-2"
          >
            {error}
          </div>
        )}

        {plans === null ? (
          <div className="py-8 flex items-center justify-center text-[#666]">
            <Loader2 size={16} className="animate-spin mr-2" />
            Loading options…
          </div>
        ) : plans.length === 0 ? (
          <p className="py-6 text-[13px] text-[#666]">
            No top-up options are available right now.
          </p>
        ) : (
          <div className="space-y-3">
            {plans.map((p) => (
              <button
                key={p.tier_key}
                type="button"
                onClick={() => buy(p.tier_key)}
                disabled={busyKey !== null}
                data-testid={`buy-credits-${p.tier_key}`}
                className="w-full text-left rounded-xl border border-[#eee] p-4 flex items-center gap-4 hover:border-[#202B60] hover:shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span
                  className="shrink-0 inline-flex items-center justify-center rounded-lg"
                  style={{ background: "#EAF0FF", color: "#202B60", width: 40, height: 40 }}
                >
                  <Sparkles size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-[#0A1628]">{p.label}</p>
                  <p className="text-[12px] text-[#666] mt-0.5">{p.tagline}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[16px] font-bold text-[#0A1628]">
                    €{Number(p.amount_eur).toFixed(2).replace(/\.00$/, "")}
                  </p>
                  {busyKey === p.tier_key ? (
                    <span className="inline-flex items-center gap-1 text-[11.5px] text-[#666] mt-1">
                      <Loader2 size={11} className="animate-spin" /> Opening…
                    </span>
                  ) : (
                    <span className="text-[11.5px] text-[#666] mt-1 block">
                      one-time
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        <p className="mt-4 text-[11.5px] text-[#888] flex items-start gap-1.5">
          <Check size={11} className="mt-[3px] shrink-0" />
          <span>
            Credits are added to your account as soon as Stripe confirms the
            payment. Refunds excluded once credits are used.
          </span>
        </p>
      </div>
    </div>
  );
}
